/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton, LinkIconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag/flag-provider';
import Heading from '@atlaskit/heading/heading';
import LinkRestrictedAccessIcon from '@atlaskit/icon-lab/core/link-restricted-access';
import CommentIcon from '@atlaskit/icon/core/comment';
import LinkIcon from '@atlaskit/icon/core/link';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { Box, Flex, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { useSideNavModal } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';

import { useAssistanceServiceParams } from '../../../../common/utils/use-assistance-service-params';

import { messages } from './messages';
import { type SharedChatLink, type SharedChatLinkType } from './sharedChatsTypes';
import { MAX_VISIBLE_SHARES, useGroupedShares } from './useGroupedShares';

type SegmenterConstructor = new (
	locale?: string,
	options?: { granularity: 'word' },
) => {
	segment: (input: string) => Iterable<{ segment: string; isWordLike?: boolean }>;
};

type FormatTruncatedTitle = (titlePrefix: string) => string;

const getWordBoundedPrefixes = (title: string, locale: string): string[] => {
	const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter;

	if (Segmenter) {
		const segmenter = new Segmenter(locale, { granularity: 'word' });
		const prefixes: string[] = [];
		let prefix = '';

		for (const segment of segmenter.segment(title)) {
			prefix += segment.segment;

			if (segment.isWordLike) {
				prefixes.push(prefix.trimEnd());
			}
		}

		return prefixes;
	}

	return (title.match(/\S+\s*/gu) ?? []).map((_, index, words) =>
		words
			.slice(0, index + 1)
			.join('')
			.trimEnd(),
	);
};

const getLongestFittingPrefix = (
	prefixes: string[],
	availableWidth: number,
	measureText: (text: string) => number,
	formatTruncatedTitle: FormatTruncatedTitle,
): string => {
	let low = 0;
	let high = prefixes.length - 1;
	let fittingPrefix = '';

	while (low <= high) {
		const mid = Math.floor((low + high) / 2);
		const candidate = formatTruncatedTitle(prefixes[mid]);

		if (measureText(candidate) <= availableWidth) {
			fittingPrefix = prefixes[mid];
			low = mid + 1;
		} else {
			high = mid - 1;
		}
	}

	return fittingPrefix;
};

const truncateUnbrokenTitle = (
	title: string,
	availableWidth: number,
	measureText: (text: string) => number,
	formatTruncatedTitle: FormatTruncatedTitle,
): string => {
	const characters = Array.from(title);
	const fittingPrefix = getLongestFittingPrefix(
		characters.map((_, index) => characters.slice(0, index + 1).join('')),
		availableWidth,
		measureText,
		formatTruncatedTitle,
	);

	return formatTruncatedTitle(fittingPrefix);
};

const truncateTitleToWidth = (
	title: string,
	availableWidth: number,
	measureText: (text: string) => number,
	formatTruncatedTitle: FormatTruncatedTitle,
	locale: string,
): string => {
	const trimmedTitle = title.trimEnd();

	if (trimmedTitle === '' || availableWidth <= 0 || measureText(trimmedTitle) <= availableWidth) {
		return title;
	}

	const wordBoundedPrefix = getLongestFittingPrefix(
		getWordBoundedPrefixes(trimmedTitle, locale),
		availableWidth,
		measureText,
		formatTruncatedTitle,
	);

	if (wordBoundedPrefix !== '') {
		return formatTruncatedTitle(wordBoundedPrefix);
	}

	return truncateUnbrokenTitle(trimmedTitle, availableWidth, measureText, formatTruncatedTitle);
};

const styles = cssMap({
	root: {
		maxWidth: '832px',
		height: '100%',
	},
	headingIconContainer: {
		display: 'flex',
		width: '40px',
		height: '40px',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		flexShrink: 0,
	},
	card: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
		flex: '1 1 0%',
		minHeight: '0px',
		overflow: 'auto',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	truncationNotice: {
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.100'),
	},
	list: {
		marginBlockStart: 0,
		marginBlockEnd: 0,
		paddingInlineStart: 0,
		listStyleType: 'none',
	},
	row: {
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		borderBottomWidth: 0,
	},
	conversationRow: {
		marginTop: token('space.200'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
	firstConversationRow: {
		marginTop: token('space.0'),
	},
	titleContainer: {
		minWidth: 0,
	},
	title: {
		minWidth: 0,
		overflow: 'hidden',
		position: 'relative',
		whiteSpace: 'nowrap',
	},
	conversationTitleButton: {
		display: 'block',
		width: '100%',
		minWidth: 0,
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	titleMeasure: {
		position: 'absolute',
		overflow: 'visible',
		pointerEvents: 'none',
		visibility: 'hidden',
		whiteSpace: 'nowrap',
		width: 'max-content',
	},
	titleIcon: {
		position: 'relative',
		display: 'inline-flex',
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
		color: token('color.text.subtle'),
		flexShrink: 0,
	},
	connectorFromPrevious: {
		'&::before': {
			position: 'absolute',
			top: token('space.negative.075'),
			bottom: token('space.300'),
			left: '50%',
			borderLeftWidth: token('border.width.selected'),
			borderLeftStyle: 'solid',
			borderLeftColor: token('color.border'),
			content: '""',
		},
	},
	connectorToNext: {
		'&::after': {
			position: 'absolute',
			top: token('space.300'),
			bottom: token('space.negative.075'),
			left: '50%',
			borderLeftWidth: token('border.width.selected'),
			borderLeftStyle: 'solid',
			borderLeftColor: token('color.border'),
			content: '""',
		},
	},
	actions: {
		flexShrink: 0,
	},
	stateContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '96px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		textAlign: 'center',
	},
});

type SharedChatLinkIconProps = {
	type: SharedChatLinkType;
	hasConnectorFromPrevious?: boolean;
	hasConnectorToNext?: boolean;
};

const SharedChatLinkIcon = ({
	type,
	hasConnectorFromPrevious = false,
	hasConnectorToNext = false,
}: SharedChatLinkIconProps): React.JSX.Element | null => {
	// Conversation rows are the group heading and render without an icon so their
	// title sits flush-left; only the per-share rows carry the chat icon.
	if (type === 'conversation') {
		return null;
	}

	return (
		<Box
			xcss={cx(
				styles.titleIcon,
				hasConnectorFromPrevious && styles.connectorFromPrevious,
				hasConnectorToNext && styles.connectorToNext,
			)}
		>
			<CommentIcon label="" color="currentColor" />
		</Box>
	);
};

type SharedChatTitleProps = {
	color: React.ComponentProps<typeof Text>['color'];
	testId: string;
	title: string;
	weight: React.ComponentProps<typeof Text>['weight'];
};

const SharedChatTitle = ({
	color,
	testId,
	title,
	weight,
}: SharedChatTitleProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const titleRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLSpanElement>(null);
	const [visibleTitle, setVisibleTitle] = useState(title);
	const formatTruncatedTitle = useCallback(
		(titlePrefix: string) => formatMessage(messages.truncatedTitle, { titlePrefix }),
		[formatMessage],
	);

	const updateVisibleTitle = useCallback(() => {
		const titleElement = titleRef.current;
		const measureElement = measureRef.current;

		if (!titleElement || !measureElement) {
			return;
		}

		const availableWidth = titleElement.getBoundingClientRect().width;
		const measureText = (text: string) => {
			measureElement.textContent = text;
			const measuredWidth = measureElement.getBoundingClientRect().width;
			measureElement.textContent = '';
			return measuredWidth;
		};
		const nextVisibleTitle = truncateTitleToWidth(
			title,
			availableWidth,
			measureText,
			formatTruncatedTitle,
			locale,
		);

		setVisibleTitle((currentVisibleTitle) =>
			currentVisibleTitle === nextVisibleTitle ? currentVisibleTitle : nextVisibleTitle,
		);
	}, [formatTruncatedTitle, locale, title]);

	useLayoutEffect(() => {
		updateVisibleTitle();

		const titleElement = titleRef.current;

		if (!titleElement || typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const resizeObserver = new ResizeObserver(updateVisibleTitle);
		resizeObserver.observe(titleElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [updateVisibleTitle]);

	return (
		<Box as="div" xcss={styles.title} ref={titleRef} testId={testId}>
			<Text color={color} weight={weight} maxLines={1}>
				{visibleTitle}
			</Text>
			<Box as="span" xcss={styles.titleMeasure} aria-hidden="true">
				<Text color={color} weight={weight} maxLines={1}>
					<span ref={measureRef} />
				</Text>
			</Box>
		</Box>
	);
};

type SharedChatLinksListProps = {
	recentLinks: SharedChatLink[];
	onPreview: () => void;
	/** Deletes the share(s) a row represents; rejects on failure. */
	onDeleteRow: (row: SharedChatLink) => Promise<void>;
	onDeleteStarted: (rowId: string, shouldRestoreFocus: boolean) => void;
	onDeleteSettled: (rowId: string, succeeded: boolean) => void;
	registerDeleteButton: (rowId: string, element: HTMLButtonElement | null) => void;
};

const SharedChatLinksList = ({
	recentLinks,
	onPreview,
	onDeleteRow,
	onDeleteStarted,
	onDeleteSettled,
	registerDeleteButton,
}: SharedChatLinksListProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const publish = usePublish('ai-mate');
	const { close: closeModal } = useSideNavModal();

	// Tracks which rows currently have a delete in flight, so their delete button
	// shows progress and we don't fire duplicate requests on repeated clicks.
	const [deletingIds, setDeletingIds] = useState<ReadonlySet<string>>(() => new Set());

	const handleOpenConversation = useCallback(
		(conversationId: string) => {
			closeModal();
			publish({
				type: 'chat-open',
				source: 'rovo-settings-modal-shared-chats',
				data: {
					channelId: conversationId,
				},
			});
		},
		[closeModal, publish],
	);

	const handleDelete = useCallback(
		async (row: SharedChatLink) => {
			setDeletingIds((prev) => new Set(prev).add(row.id));
			try {
				await onDeleteRow(row);
				showFlag({
					title: formatMessage(messages.accessUpdatedFlagTitle),
					icon: <StatusSuccessIcon label="" color={token('color.icon.success')} />,
					isAutoDismiss: true,
				});
				onDeleteSettled(row.id, true);
			} catch {
				showFlag({
					title: formatMessage(messages.accessUpdateErrorFlagTitle),
					description: formatMessage(messages.accessUpdateErrorFlagDescription),
					icon: <StatusErrorIcon label="" color={token('color.icon.danger')} />,
					isAutoDismiss: true,
				});
				onDeleteSettled(row.id, false);
			} finally {
				setDeletingIds((prev) => {
					const next = new Set(prev);
					next.delete(row.id);
					return next;
				});
			}
		},
		[formatMessage, onDeleteRow, onDeleteSettled, showFlag],
	);

	return (
		<Box as="ul" xcss={styles.list}>
			{recentLinks.map((link, index) => {
				// Only per-share rows carry an opaque token that maps to a single
				// sender-preview URL; conversation rows aggregate multiple shares and
				// have no single link to open.
				const canOpen = link.type === 'prompt' && link.href !== '';
				const isDeleting = deletingIds.has(link.id);
				const hasConnectorFromPrevious =
					index > 0 && recentLinks[index - 1]?.conversationId === link.conversationId;
				const hasConnectorToNext =
					link.type === 'prompt' &&
					index < recentLinks.length - 1 &&
					recentLinks[index + 1]?.conversationId === link.conversationId;

				return (
					<Box
						as="li"
						xcss={cx(
							styles.row,
							link.type === 'conversation' && styles.conversationRow,
							index === 0 && styles.firstConversationRow,
						)}
						key={link.id}
					>
						<Flex alignItems="center" gap="space.150" justifyContent="space-between">
							<Inline
								space="space.150"
								alignBlock="center"
								grow="fill"
								xcss={styles.titleContainer}
							>
								<SharedChatLinkIcon
									type={link.type}
									hasConnectorFromPrevious={hasConnectorFromPrevious}
									hasConnectorToNext={hasConnectorToNext}
								/>
								{link.type === 'conversation' ? (
									<Pressable
										aria-label={link.title}
										onClick={() => handleOpenConversation(link.conversationId)}
										testId={`shared-chat-conversation-${link.id}`}
										xcss={styles.conversationTitleButton}
									>
										<SharedChatTitle
											color="color.text"
											testId={`shared-chat-title-${link.id}`}
											title={link.title}
											weight="semibold"
										/>
									</Pressable>
								) : (
									<SharedChatTitle
										color="color.text.subtle"
										testId={`shared-chat-title-${link.id}`}
										title={link.title}
										weight="regular"
									/>
								)}
							</Inline>
							<Inline space="space.075" alignBlock="center" xcss={styles.actions}>
								{canOpen && (
									<LinkIconButton
										appearance="subtle"
										spacing="compact"
										href={link.href}
										target="_blank"
										onClick={onPreview}
										label={formatMessage(messages.openSharedChatLabel, { title: link.title })}
										icon={(iconProps) => <LinkExternalIcon {...iconProps} label="" />}
									/>
								)}
								{link.type === 'prompt' && (
									<IconButton
										type="button"
										appearance="subtle"
										spacing="compact"
										isTooltipDisabled={false}
										isLoading={isDeleting}
										onClick={(event) => {
											onDeleteStarted(
												link.id,
												event.currentTarget.ownerDocument.activeElement === event.currentTarget,
											);
											void handleDelete(link);
										}}
										ref={(element) => {
											registerDeleteButton(link.id, element);
										}}
										testId={`shared-chat-delete-${link.id}`}
										label={formatMessage(messages.deleteSharedChatLinkLabel, {
											title: link.title,
										})}
										tooltip={{
											content: formatMessage(messages.unshareTooltip),
											position: 'right',
										}}
										icon={(iconProps) => (
											<LinkRestrictedAccessIcon
												{...iconProps}
												label=""
												testId="shared-chat-restricted-access-icon"
											/>
										)}
									/>
								)}
							</Inline>
						</Flex>
					</Box>
				);
			})}
		</Box>
	);
};

type SharedChatsContentProps = {
	/**
	 * Pre-supplied rows. When provided, the component renders these directly and
	 * skips its own data-fetching (useful for Storybook and tests). When omitted,
	 * rows are fetched from the grouped-shares API.
	 */
	recentLinks?: SharedChatLink[];
};

type DeleteFocusRequest = {
	deletedId: string;
	nextId?: string;
	previousId?: string;
	shouldRestoreFocus: boolean;
	status: 'pending' | 'settled';
};

export const SharedChatsContent = ({
	recentLinks: recentLinksProp,
}: SharedChatsContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const recentLinksHeadingId = useId();
	const cardRef = useRef<HTMLElement>(null);
	const deleteButtonRefs = useRef(new Map<string, HTMLButtonElement>());
	const [deleteFocusRequest, setDeleteFocusRequest] = useState<DeleteFocusRequest | null>(null);

	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-settings-modal-side-nav-shared-chats',
	});

	const {
		recentLinks: fetchedRecentLinks,
		hasMore,
		isLoading,
		error,
		onRetry,
		onPreview,
		onDeleteRow,
	} = useGroupedShares({
		assistanceServiceParams,
		isEnabled: recentLinksProp === undefined,
	});

	// When rows are supplied by the caller, skip fetch-driven states entirely.
	const isControlled = recentLinksProp !== undefined;
	const recentLinks = recentLinksProp ?? fetchedRecentLinks;

	const handleDeleteStarted = useCallback(
		(rowId: string, shouldRestoreFocus: boolean) => {
			const promptLinks = recentLinks.filter((link) => link.type === 'prompt');
			const rowIndex = promptLinks.findIndex((link) => link.id === rowId);

			setDeleteFocusRequest({
				deletedId: rowId,
				nextId: promptLinks[rowIndex + 1]?.id,
				previousId: promptLinks[rowIndex - 1]?.id,
				shouldRestoreFocus,
				status: 'pending',
			});
		},
		[recentLinks],
	);

	const handleDeleteSettled = useCallback((rowId: string, succeeded: boolean) => {
		setDeleteFocusRequest((request) => {
			if (request?.deletedId !== rowId) {
				return request;
			}

			return succeeded ? { ...request, status: 'settled' } : null;
		});
	}, []);

	const registerDeleteButton = useCallback((rowId: string, element: HTMLButtonElement | null) => {
		if (element) {
			deleteButtonRefs.current.set(rowId, element);
		} else {
			deleteButtonRefs.current.delete(rowId);
		}
	}, []);

	useLayoutEffect(() => {
		if (!deleteFocusRequest || deleteFocusRequest.status !== 'settled') {
			return;
		}

		const deletedRowStillExists = recentLinks.some(
			(link) => link.id === deleteFocusRequest.deletedId,
		);
		const ownerDocument = cardRef.current?.ownerDocument;
		const focusWasLost =
			ownerDocument?.activeElement === ownerDocument?.body || ownerDocument?.activeElement === null;

		if (!deletedRowStillExists && deleteFocusRequest.shouldRestoreFocus && focusWasLost) {
			const nextButton = deleteFocusRequest.nextId
				? deleteButtonRefs.current.get(deleteFocusRequest.nextId)
				: undefined;
			const previousButton = deleteFocusRequest.previousId
				? deleteButtonRefs.current.get(deleteFocusRequest.previousId)
				: undefined;

			(nextButton ?? previousButton ?? cardRef.current)?.focus();
		}

		setDeleteFocusRequest(null);
	}, [deleteFocusRequest, recentLinks]);

	const showLoading = !isControlled && isLoading && recentLinks.length === 0;
	const showError = !isControlled && !isLoading && !!error;
	const showEmpty = !isControlled && !isLoading && !error && recentLinks.length === 0;
	const showList = isControlled || (!error && recentLinks.length > 0);

	const content = (
		<React.Fragment>
			{showLoading && (
				<Box xcss={styles.stateContainer}>
					<Spinner label={formatMessage(messages.loadingLabel)} />
				</Box>
			)}

			{showError && (
				<Box xcss={styles.stateContainer}>
					<Stack space="space.150" alignInline="center">
						<Text color="color.text.subtle">{formatMessage(messages.errorMessage)}</Text>
						<Button appearance="default" onClick={onRetry}>
							{formatMessage(messages.retryButton)}
						</Button>
					</Stack>
				</Box>
			)}

			{showEmpty && (
				<Box xcss={styles.stateContainer}>
					<Text color="color.text.subtle">{formatMessage(messages.emptyMessage)}</Text>
				</Box>
			)}

			{showList && hasMore && (
				<Box xcss={styles.truncationNotice}>
					<Text size="small" color="color.text.subtle">
						{formatMessage(messages.sharesTruncatedMessage, { limit: MAX_VISIBLE_SHARES })}
					</Text>
				</Box>
			)}

			{showList && (
				<SharedChatLinksList
					recentLinks={recentLinks}
					onPreview={onPreview}
					onDeleteRow={onDeleteRow}
					onDeleteStarted={handleDeleteStarted}
					onDeleteSettled={handleDeleteSettled}
					registerDeleteButton={registerDeleteButton}
				/>
			)}
		</React.Fragment>
	);

	return (
		<Stack space="space.300" xcss={styles.root}>
			<Flex gap="space.150" alignItems="center">
				<Box xcss={styles.headingIconContainer}>
					<LinkIcon label="" color="currentColor" />
				</Box>
				<Stack>
					<Heading as="h2" size="small" id={recentLinksHeadingId}>
						{formatMessage(messages.heading)}
					</Heading>
					<Text size="medium" color="color.text.subtle">
						{formatMessage(messages.description)}
					</Text>
				</Stack>
			</Flex>

			<Box
				as="section"
				xcss={styles.card}
				aria-labelledby={recentLinksHeadingId}
				ref={cardRef}
				tabIndex={-1}
			>
				{content}
			</Box>
		</Stack>
	);
};
